import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import BusinessAssociateAgreement from "components/baa/businessAssociateAgreement";
import PrivacyPolicyRedirect from "components/privacypolicy/PrivacyPolicyRedirect";
import TermsOfUse from "components/termsofuse/termsOfUse";
import { pageTransition, pageVariants } from "config/helpers/animation_helper";
import { unixDate } from "config/helpers/date_helper";
import {
  CHECKOUT,
  HOME,
  PATIENT,
  SETTINGS,
  STORE,
  getPathNames,
} from "config/helpers/router_helper";
import {
  INSIGHTS,
  RATING_SCALES,
  TEMPLATES,
} from "config/helpers/router_helper_v2";
import { motion } from "framer-motion";
import _ from "lodash";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generate_notification } from "redux/actions/AppActions";
import "style_v2/global.scss";
import { AuthRoute, DashboardRoute } from "./RouterHelpers";
import SessionInvalid from "screens/fallbacks/SessionInvalid";

const USER_AUTHENTICATION = React.lazy(() => import("screens/login/v2/Login"));
const ERROR_PAGE = React.lazy(() => import("components/404/NotFound"));
const TRIAL_PAGE = React.lazy(() =>
  import("screens/login/v2/TrialSubscriptionPage")
);
const NOKI_PLANS = React.lazy(() => import("screens/store/Plans"));
const STRIPE_INITIALIZER = React.lazy(() =>
  import("screens/store/stripe/StripeInitializer")
);

const RootRouter = () => {
  // useIntegratedEHR();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const unsubscribe = toast.onChange((messages) => {
      if (messages?.status === "removed") return;
      if (!messages?.data?.ignoreNotification) {
        dispatch(
          generate_notification({
            message: messages?.content,
            isRead: false,
            addedOn: unixDate(new Date()),
            id: messages?.id,
          })
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    let paths = getPathNames();
    let path = paths[paths.length - 1] || "";
    document.title = `Noki | ${_.capitalize(path.replaceAll("_", " "))}`;
  }, [location]);

  const hasChildObject = (obj) => {
    let child = [];
    Object.keys(obj).map((m) => {
      if (typeof obj[m] === "object" && !obj[m].hasOwnProperty("$$typeof")) {
        Object.keys(obj[m]).map((p) => {
          child.push({ component: obj[m][p], parent: m, name: p });
          return true;
        });
      }
      return true;
    });
    return child;
  };

  const AnimationExtendedComponent = (props) => {
    let Module = props.jsx;
    let ModuleWrapper = props.wrapper;
    let ChildModuleWrapper = props.childwrapper;
    return (
      <>
        <ModuleWrapper />
        <motion.div
          key={props.componentKey}
          initial="initial"
          animate="in"
          variants={pageVariants}
          transition={pageTransition}
        >
          {(ChildModuleWrapper && <ChildModuleWrapper />) || null}
          <Module />
        </motion.div>
      </>
    );
  };

  const EmptyWrapper = () => {
    return <></>;
  };

  const getParentWrapper = (mod, page = false) => {
    if (page === false) {
      if (
        COMMON_WRAPPERS[mod] &&
        COMMON_WRAPPERS[mod].hasOwnProperty("WRAPPER")
      ) {
        return COMMON_WRAPPERS[mod].WRAPPER;
      } else return EmptyWrapper;
    } else {
      if (
        COMMON_WRAPPERS[mod] &&
        COMMON_WRAPPERS[mod][page] &&
        COMMON_WRAPPERS[mod][page].hasOwnProperty("WRAPPER")
      ) {
        return COMMON_WRAPPERS[mod][page].WRAPPER;
      } else return EmptyWrapper;
    }
  };

  const makePath = (mod, page, sub = false) => {
    if (mod === PATIENT && page === "VISITS_NEOMED") {
      return "patients/visits/:id/neomed";
    }

    if (mod === PATIENT && page === "VISITS_CYBER") {
      return "patients/visits/:id/cybermed";
    }

    // keep the rest of the existing function below this...
    let path = "";
    path += _.lowerCase(mod).replaceAll(" ", "_") + "/";

    if (page?.split("_")?.includes("EDIT")) {
      let newUrl = page
        ?.split("_")
        ?.filter((item) => item !== "EDIT")
        .join("_");
      path += _.lowerCase(newUrl).replaceAll(" ", "_") + "/";
      path += ":id";
      return path;
    }

    path += _.lowerCase(page).replaceAll(" ", "_") + "/";
    if (sub) {
      path += _.lowerCase(sub).replaceAll(" ", "_");
    }
    return path;
  };

  const handleBeforeUnload = () => {
    return "Are you sure you want to leave this page?";
  };

  return (
    <>
      <ToastContainerComponent newestOnTop />
      {/* <BrowserEventHandler
        onBeforeUnload={handleBeforeUnload}
      // onUnload={handleUnload}
      /> */}
      <Routes>
        {/* {/ auth routes /} */}
        {Object.keys(MODULES).map((mod, idx) => {
          return (
            <React.Fragment key={`${mod}_${idx}`}>
              {Object.keys(MODULES[mod]).map((page, pId) => {
                let childModules = hasChildObject(MODULES[mod][page]);
                let ParentWrapper = getParentWrapper(mod);
                return (
                  <>
                    {childModules.length === 0 ? (
                      <>
                        {Object.keys(MODULES[mod][page]).map((sub, subId) => {
                          let ChildWrapper = getParentWrapper(mod, page);
                          return (
                            <>
                              {hasChildObject(MODULES[mod][page][sub])
                                .length !== 0 ? (
                                <Route
                                  key={`${page}_${idx}_${pId}_${subId}`}
                                  path={makePath(mod, page, sub)}
                                  index
                                  element={
                                    <DashboardRoute
                                      component={
                                        <AnimationExtendedComponent
                                          componentKey={`${mod}/${page}`}
                                          jsx={MODULES[mod][page][sub]}
                                          wrapper={ParentWrapper}
                                          childwrapper={ChildWrapper}
                                        />
                                      }
                                    />
                                  }
                                />
                              ) : (
                                <></>
                              )}
                            </>
                          );
                        })}
                      </>
                    ) : (
                      <></>
                    )}
                    <Route
                      key={`${page}_${idx}_${pId}`}
                      path={makePath(mod, page)}
                      index
                      element={
                        <DashboardRoute
                          component={
                            <AnimationExtendedComponent
                              componentKey={`${mod}/${page}`}
                              jsx={MODULES[mod][page]}
                              wrapper={ParentWrapper}
                            />
                          }
                        />
                      }
                    />
                  </>
                );
              })}
            </React.Fragment>
          );
        })}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          index
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/signup"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/verify/:code"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/reset-password/:code"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/signup/confirm"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        {/* <Route
          path="/signup/user-exists"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/signup/failed"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        /> */}
        <Route
          path="/fail-verify"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/fail-link-used"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route path="/plans" element={<NOKI_PLANS />} />
        <Route path="/checkout" element={<STRIPE_INITIALIZER />} />
        {/* <Route
          path="/checkout"
          element={<Checkout/>}
        /> */}
        {/* <Route
          path="/verify-otp"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/complete-registration"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        /> */}
        <Route
          path="/reset"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/privacypolicy"
          //element={<AuthRoute component={PRIVACY_POLICY} />}
          element={<PrivacyPolicyRedirect />}
        />
        <Route
          path="/terms-of-use"
          //element={<AuthRoute component={TERMS_OF_USE} />}
          element={<TermsOfUse />}
        />
        <Route
          path="/business-associate-agreement"
          //element={<AuthRoute component={BUSINESS_ASSOCIATE_AGREEMENT} />}
          element={<BusinessAssociateAgreement />}
        />
        <Route path="/welcome" element={<TRIAL_PAGE />} />
        {/*<Route path="/demo" element={<RTCDemo />} />
        <Route path="/demo1" element={<CoquiTest />} />
        <Route path="/demo2" element={<MultiAgentDemo />} />
        <Route path="/demo2/agent" element={<AgentPage />} />*/}
        <Route path="/session/invalid" element={<SessionInvalid />} />
        <Route path="*" element={<ERROR_PAGE />} />
      </Routes>
    </>
  );
};

const COMMON_WRAPPERS = {
  // [HOME]: {
  //     WRAPPER: React.lazy(() => import('screens/appointments/AppointmentsTabWrapper')),
  //     SOME: {
  //         WRAPPER: React.lazy(() => import('screens/appointments/AppointmentsTabWrapperSub')),
  //     }
  // },
  // [APPOINTMENTS]: {
  //   WRAPPER: React.lazy(() =>
  //     import("screens/appointments/AppointmentsTabWrapper")
  //   ),
  // },
  // [EHR]: {
  //   WRAPPER: React.lazy(() => import("screens/ehr/EHRTabWrapper")),
  //   HISTORY: {
  //     WRAPPER: React.lazy(() =>
  //       import("screens/ehr/history/HistoryTabWrapper")
  //     ),
  //   },
  // },
  // [TASKS]: {
  //   WRAPPER: React.lazy(() => import("screens/tasks/TasksTabWrapper")),
  // },
};

const MODULES = {
  [HOME]: {
    DASHBOARD: React.lazy(() => import("screens/home/Dashboard_v3")),
  },
  [INSIGHTS]: {
    // DASHBOARD: React.lazy(() => import("screens/home/Dashboard")),
    INFO: React.lazy(() => import("screens/home/Dashboard_v2")),
    // SOME: {
    //   OTHER: React.lazy(() => import("screens/appointments/Appointments")),
    //   OTHERN: React.lazy(() => import("screens/appointments/Appointments")),
    //   OTHERP: React.lazy(() => import("screens/appointments/Appointments")),
    // },
  },
  [PATIENT]: {
    // LIST: React.lazy(() => import("screens/patients/FhirPatientList")),
    // VISITS_EDIT: React.lazy(() =>
    //   import("screens/patients/clinicalVisits/ClinicalVisits")
    // ),
    DASHBOARD_EDIT: React.lazy(() =>
      import("screens/patients_v2/dashboard/PatientDashboard")
    ),
    LIST: React.lazy(() => import("screens/patients_v2/FhirPatientList")),
    VISITSZ_EDIT: React.lazy(() =>
      import("screens/patients_v2/clinicalVisits/ClinicalVisits")
    ),
    VISITS_EDIT: React.lazy(() => import("screens/patientInfo/PatientWrapper")),
    VISITS_CYBER: React.lazy(() =>
      import("screens/patientInfo/CyberPatientWrapper")
    ),
    VISITS_NEOMED: React.lazy(() =>
      import("screens/patientInfo/NeomedPatientWrapper")
    ),
    PROFILE_EDIT: React.lazy(() =>
      import("screens/patientInfo/infoArea/DetailedPatientScreen")
    ),
  },
  [TEMPLATES]: {
    "": React.lazy(() => import("screens/templates/TemplatesPage")),
    NEW: React.lazy(() => import("screens/templates/CreateTemplatePage")),
    PREVIEW: React.lazy(() => import("screens/templates/TemplatePreviewPage")),
  },
  [RATING_SCALES]: {
    "": React.lazy(() => import("screens/ratingScales")),
    SCALE_EDIT: React.lazy(() =>
      import("screens/ratingScales/RatingScalePage")
    ),
  },
  [SETTINGS]: {
    // ME: React.lazy(() => import("screens/settings/SettingsPage")),
    ME: React.lazy(() => import("screens/settings/SettingsPage_v2")),
    // RECENTLY_DELETED_ENCOUNTERS: React.lazy(() => import("screens/patients_v2/ArchivedEncounterList")),
    ARCHIVED_ENCOUNTERS: React.lazy(() =>
      import("screens/patients_v2/ArchivedEncounterList")
    ),
    PROFILE: React.lazy(() => import("components/profile/ProfileDetails")),
    SSO: React.lazy(() => import("screens/settings_v2/pages/AuthSSOLayout")),
    VIM_CONNECTION_FORM: React.lazy(() =>
      import("screens/settings_v2/pages/vim/VimConnectForm")
    ),
    VIM_ACTIVATION_LINK: React.lazy(() =>
      import("screens/settings_v2/pages/vim/VimActivation")
    ),
    VIM_INSTALLATION: React.lazy(() =>
      import("screens/settings_v2/pages/vim/VimInstallation")
    ),
  },
  [STORE]: {
    PLANS: NOKI_PLANS,
    // PAYMENT_SUCCESS:React.lazy(() => import("screens/store/PaymentStatusScreens")),
    CHECKOUT: STRIPE_INITIALIZER,
  },
  [CHECKOUT]: {
    RETURN: React.lazy(() => import("screens/store/PaymentStatus")),
  },
};

export default RootRouter;

const ToastContainerComponent = (props) => {
  const theme = useSelector((state) => state?.app?.theme);
  const { isTokenExpired } = useSelector((state) => state?.token);
  if (isTokenExpired) {
    return <></>;
  }

  return <ToastContainer theme={theme} {...props} />;
};
